import React from 'react';
import styled from 'styled-components';

import Row from '../../components/Common/Row';
import Card from '../../components/Common/Card';
import Typography from 'kingpin/atoms/Typography';

import Button from 'kingpin/atoms/Button';
import Kpis from '../../components/Scorecard/Kpis';
import ScorecardPeriodsSelector from '../../components/Scorecard/ScorecardPeriodsSelector';
import {
  KPI_ROW_HEIGHT,
  KPI_ROW_MARGIN_BOTTOM,
} from '../../components/Scorecard/Kpis/KpiRowRight/constants';
import UserBadgesRow from '../../components/Scorecard/AssignedUsers';
import ScorecardMoreMenu from './ScorecardMoreMenu';
import ScorecardQuickFiltersDisplay from '../../components/Scorecard/QuickFilters/ScorecardQuickFiltersDisplay';
import { ResourceFavouriteButton } from '../../components/ResourceTopBar';
import PermissionGates from '../../components/PermissionGates';
import PERMISSIONS from '../../permissionDefinitions';
import MetricsInfoButton from '../../components/MetricsInfoButton';
import TasksCard from 'components/WorkSpaces/TasksCard';
import { TOP_BAR_HEIGHT } from '../../constants';
import ShareContentButton from '../../components/ShareContentButton';
import PortalAccountsGate from '../../components/PortalAccountsGate';
import { ScorecardFreshnessTooltip } from '../../components/DatasetFreshness';
import V3ContentBackButton from '../V3/Content/V3ContentBackButton';
import V3PublishOrShareButton from '../V3/Content/V3PublishOrShareButton';
import V3PrivateIndicator from '../V3/Content/V3PrivateIndicator';

const BOTTOM_CONTENT_CARDS_HEIGHT = `calc(100vh - ${
  (KPI_ROW_HEIGHT + KPI_ROW_MARGIN_BOTTOM) * 5
}px - 100px - 98px)`;

const WORKSPACE_HEIGHT = `calc(100vh - ${TOP_BAR_HEIGHT * 3}px)`;
const SCORECARD_SHOW_HEIGHT = `calc(100vh - ${TOP_BAR_HEIGHT}px)`;

const Wrapper = styled.div<{ isInWorkSpace?: boolean }>`
  width: 100%;
  height: ${(props) =>
    props.isInWorkSpace ? WORKSPACE_HEIGHT : SCORECARD_SHOW_HEIGHT};
  overflow-y: ${(props) => (props.isInWorkSpace ? 'unset' : 'scroll')};
  padding: 16px 24px;
  position: relative;
`;

const TopBar = styled.div<{
  isInWorkspace?: boolean;
}>`
  height: ${TOP_BAR_HEIGHT}px;
  padding: 0px 24px;
  background-color: var(--color-bg-primary);
  width: 100%;
  border-bottom: 1px solid var(--color-border-secondary);

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const BackButton = ({
  goBack,
  isInWorkSpace,
  isInTargets,
}: {
  goBack: () => void;
  isInWorkSpace?: boolean;
  isInTargets?: boolean;
}) => {
  if (!isInWorkSpace && !isInTargets) {
    return (
      <Button
        size={'Small'}
        type={'Ghost'}
        label={'Back'}
        icon={'arrow-left'}
        onClick={goBack}
      />
    );
  }

  return null;
};

const ScorecardShow = ({
  scorecard,
  goBack,
  onEditScorecardClicked,
  kpisListDivParentScrollerRef,
  isInWorkSpace,
  isInTargets,
}: {
  scorecard: Scorecards.Scorecard;
  goBack: () => void;
  onEditScorecardClicked: () => void;
  isMobile: boolean;
  kpisListDivParentScrollerRef: React.MutableRefObject<HTMLDivElement | null>;
  isInWorkSpace?: boolean;
  isInTargets?: boolean;
}) => {
  return (
    <div style={{ height: '100%' }}>
      <TopBar isInWorkspace={isInWorkSpace}>
        <div>
          <Row centerAlign style={{ gap: 8 }}>
            <V3ContentBackButton
              fallback={
                <BackButton
                  goBack={goBack}
                  isInTargets={isInTargets}
                  isInWorkSpace={isInWorkSpace}
                />
              }
            />

            <div>
              <Typography.Header type="H3">{scorecard.title}</Typography.Header>
            </div>
            <ResourceFavouriteButton
              type="scorecard"
              typeId={scorecard.id}
              name={scorecard.title}
            />
            <V3PrivateIndicator />
          </Row>
        </div>
        <Row style={{ justifyContent: 'flex-end', gap: 8 }} centerAlign>
          <PortalAccountsGate>
            <ShareContentButton
              typeId={scorecard.id}
              type={'scorecard'}
              contentName={scorecard.title}
              hasDivider
            />
          </PortalAccountsGate>
          <div style={{ width: 200 }}>
            <ScorecardPeriodsSelector />
          </div>
          {scorecard.assigneeId.length > 0 && (
            <div style={{ marginRight: 24 }}>
              <UserBadgesRow userIds={scorecard.assigneeId} />
            </div>
          )}

          <PermissionGates.Has
            requiredPermission={PERMISSIONS.SCORECARDS.CRUD_SCORECARD}
            fallback={<V3PublishOrShareButton />}
          >
            <Row style={{ gap: 8 }} centerAlign>
              <Button
                type="Tertiary"
                size="Small"
                label={'Edit KPI List'}
                onClick={onEditScorecardClicked}
                isDisabled={false}
                isLoading={false}
              />
              <V3PublishOrShareButton />
              <ScorecardMoreMenu
                scorecard={scorecard}
                isEditHidden
                isQuickFiltersEnabled
              />
            </Row>
          </PermissionGates.Has>
          <ScorecardFreshnessTooltip />
          <MetricsInfoButton visualisation={scorecard} />
        </Row>
      </TopBar>
      <ScorecardQuickFiltersDisplay />
      <Wrapper
        className="hiding-scrollbar"
        ref={kpisListDivParentScrollerRef}
        isInWorkSpace={isInWorkSpace}
      >
        <Card
          style={{
            height: '100%',
            marginBottom: isInWorkSpace ? 0 : 16,
            display: 'flex',
          }}
        >
          <Kpis />
        </Card>
        {!isInWorkSpace && (
          <Row
            style={{
              display: 'flex',
              minHeight: 'calc(50% - 8px)',
              width: '100%',
              flex: 1,
              height: BOTTOM_CONTENT_CARDS_HEIGHT,
            }}
          >
            <TasksCard isFullWidth />
          </Row>
        )}
      </Wrapper>
    </div>
  );
};

export default ScorecardShow;
